/**
 * Part del State comú per tots els mapes
 * Component: BaseState.ts
 * Llenguatge: TypeScript
 * Autors: Xavier Naval
 * Versió: 1.0
 * Data: 26/04/2022
 * (c) Ajuntament de Manresa - abr 2022 - Departament de Sistemes d'Informació
 */

import { observable, action } from 'mobx';
import AjtAjaxAxios from 'ajt-ajax';
import proj4 from 'proj4';
import { register } from 'ol/proj/proj4';
import OlEvent from 'ol/events/Event';
import OlLayerImage from 'ol/layer/Image';
import OlImageSource from 'ol/source/Image';
import OlLayerTile from 'ol/layer/Tile';
import OlTileSource from 'ol/source/Tile';
import OlLayerVector from 'ol/layer/Vector';
import OlProjection from 'ol/proj/Projection';
import OlSourceImageWMS from 'ol/source/ImageWMS';
import OlSourceTileWMS from 'ol/source/TileWMS';
import OlSourceWMTS, { Options } from 'ol/source/WMTS';
import OlSourceVector from 'ol/source/Vector';
import OlStyleStyle from 'ol/style/Style';
import OlStyleIcon from 'ol/style/Icon';
import OlView from 'ol/View';
import OlMap from 'ol/Map';
import OlFeature from 'ol/Feature';
import OlGeomPoint from 'ol/geom/Point';
import WMTSCapabilities from 'ol/format/WMTSCapabilities';
import MapBrowserEvent from 'ol/MapBrowserEvent';
import IconAnchorUnits from 'ol/style/IconAnchorUnits';
import { Extent, getCenter } from 'ol/extent';
import { Coordinate } from 'ol/coordinate';
import { optionsFromCapabilities } from 'ol/source/WMTS';
import { IPropsMapa } from '../interfaces/IPropsMapa';
import { IMapConfig, EmptyMapConfig } from '../interfaces/IMapConfig';
import RefCadAdrWidgetAPI from '../api/RefCadAdrWidgetAPI';
import Util from '../config/util';
import { ILayer, ILayerGroup } from '../interfaces/ILayer';
import md5 from 'md5';

// Com que les icones segona a on donaven problemes i no les carregava, les he passat a url (primer els he convertit paleta indexada per que no ocupin tant)
// import myIcon from '../img/myIcon.png';
const myIcon = `data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABsAAAAnCAMAAADJssOxAAAASFBMVEV/AAAREREUFBQXFxcZGRkaGhodHR0fHx8iIiIjIyMlJSUmJiYoKCgrKysuLi7/fgCjo6O0\
tLS6urq/v7/AwMDGxsbLy8vR0dFNFlRHAAAAAXRSTlMAQObYZgAAAOZJREFUOMuVk9sSgjAMREOQtgRKEAX+/09FBmJCWx33CeYwm80FANF9RMRxgkRzTxiZOdbUzxYFxx+5oMhKbEWroMBXhRMSp6KjFue015xdlrl32l59Oy\
iPXldzft1i1ariHbUJwHTCeoIxHs8ebLQ4Al47km5R2CBTGv5i4rl4Yd+ySObmmfQgFtwE07tfAGJhZnEzcVV21pUrre9cIJX3l1/gMXm9QbO9X7eUuRh1oZS/sl0NGoSNvvnOsM78DybOJ0jiah2ta5f+gZTJeOpx29HtkWHg\
zWlc1G6oBSjBItpuZ9GvL66YJV0Xw6fUAAAAAElFTkSuQmCC`;
// import myShadow from '../img/myShadow2.png';
const myShadow = `data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAC0AAAAaCAMAAAAUuyW9AAAAgVBMVEUAAACLi4uMjIyNjY2Ojo6Pj4+QkJCRkZGSkpKTk5OUlJSVlZWWlpaXl5eYmJiZmZmamp\
qbm5ucnJyenp6fn5+goKChoaGioqKjo6OkpKSlpaWmpqanp6eoqKipqamqqqqrq6usrKytra2urq6vr6+wsLCxsbGysrKzs7O1tbW4uLh+i9hgAAAAAXRSTlMAQObYZgAAAUJJREFUOMuNlOlWgzAQhUMmO0lAsCxVrNW6vv8D\
miNtMgVSvYdfl4/JZBYIyeh1bBsmpFYMTF1JklfvNZPACkrDQykEybtpmzXeAMxcEjDTrNG9D28W5FnqYcF6b1h8u/wGqmsYOEdHCyk5YNy8IZanYCBLN3bD51RrxOuPBDci+fb4cnYnhegEPzoWc+DJnlyERSpKF13wB5TeoF\
Po6LcqJq16BD83EE8soytT4QxqGot3Ad1dzKNLMBXj9+yOUxVNpuo0FwaXVbjutPf3Vilc6wi/u0XTQHDOLqNShJQpukqraF4F197ihkvIskD17ulqOlqRiRquoOVilE4WMqhAJY4NMJvZgut3G6tS8xXLedVX24slVglwU+c2\
dsShmbK6Gm7sdzvTBYSm2Gb8IjdVwu8vgJRCkr8VVlFIW3fkP/oBkccOxYGw+IkAAAAASUVORK5CYII=`;

export default abstract class BaseState {
    // Constructor
    constructor(props: IPropsMapa, configFile: string) {
        this._props = props;

        // Llegim el fitxer de configuració i creem el mapa
        const cfgFile = `${process.env.PUBLIC_URL}/${configFile}_${this._props.appState.entorn}.json`;
        fetch(cfgFile)
        .catch()
        .then(r => r.status !== 404 ? r.text() : '')
        .then(cfg => {
            try {
                this.mapConfig = (JSON.parse(cfg) as IMapConfig);
                    
                // Si cal posem l'ajuda
                if (this.mapConfig.helpUrl) {
                    this._props.appState.sessState.recurs.RECURLHELP = this.mapConfig.helpUrl;
                }
        
                // Si cal posem el títol
                if (this.mapConfig.title) {
                    this._props.appState.config.titol = this.mapConfig.title;
                //     this._props.appState.pantState.label = this.mapConfig.title;
            }

                // Creem la projecció
                // ol.proj.setProj4(proj4);
                proj4.defs(this.mapConfig.projCode, this.mapConfig.proj4Defs);
                this.olproj = new OlProjection({ code: this.mapConfig.projCode, units: 'm', metersPerUnit: 1.0,
                    getPointResolution: function(resolution: number) {
                        return resolution;
                    } 
                });
                register(proj4);

                // Mirem si tenim capes WMTS per fer la lectura de les capabilities
                const wmtslys: Array<Promise<object>> = [];
                this.mapConfig.baseLayers.forEach((bl: ILayer) => { // Capes base
                    if (bl.type === 'wmts') {
                        this.addWMTSLayerCapRequest(wmtslys, bl);
                    }
                });
                this.mapConfig.refLayers.forEach((rl) => { // Capes de referència
                    if (rl.type === 'wmts') {
                        this.addWMTSLayerCapRequest(wmtslys, rl as ILayer);
                    } else if (rl.type === 'grp') {
                        (rl as ILayerGroup).layers.forEach((rl2) => { // Capes de referència dins d'un grup
                            if (rl2.type === 'wmts' || rl2.type === 'grp') {
                                this.addWMTSLayerCapRequest(wmtslys, rl2);
                            }
                        })
                    }
                });
                if (this.mapConfig.nomsLayer && this.mapConfig.nomsLayer.type === 'wmts') { // La capa de noms també pot ser WMTS
                    this.addWMTSLayerCapRequest(wmtslys, this.mapConfig.nomsLayer);
                }
                // Llegim les capabilities de les capes WMTS
                if (wmtslys.length > 0) { // Hi ha capes WMTS
                    Promise.allSettled(wmtslys)
                    .then(() => {
                        // Creem el mapa i marquem que hem llegit bé la configuració
                        this.refCadWebApi = new RefCadAdrWidgetAPI(this.mapConfig.refCadWebApiUrl);
                        this.createMap();
                        this.configOk = true;
                    });
                } else { // No hi ha capes WMTS
                    // Creem el mapa i marquem que hem llegit bé la configuració
                    this.refCadWebApi = new RefCadAdrWidgetAPI(this.mapConfig.refCadWebApiUrl);
                    this.createMap();
                    this.configOk = true;
                }
            } catch (error) {
                console.log('Falta fitxer ' + cfgFile + ' o té un format incorrecte.' + error);
            }
        });
    }

    // Afegeix un layer de tipus WMTS per demanar les capabilities
    private addWMTSLayerCapRequest(wmtslys: Array<Promise<object>>, ly: ILayer | ILayerGroup) {
        if (ly.type === 'wmts') {
            const ll = ly as ILayer;
            wmtslys.push(
                fetch(ll.url)
                .then(function (response: Response): Promise<string> {
                    return response.text();
                })
                .then(function (text: string) {
                    const result = new WMTSCapabilities().read(text);
                    const options = optionsFromCapabilities(result, {
                        layer: ll.layers,
                        matrixSet: ll.matrixSet
                    });
                    ll.cap = options;
                    return {};
                })
            );
        } else if (ly.type === 'grp') {
            (ly as ILayerGroup).layers.forEach((rl2) => { // Capes de referència dins d'un grup
                if (rl2.type === 'wmts' || rl2.type === 'grp') {
                    this.addWMTSLayerCapRequest(wmtslys, rl2);
                }
            })
        }
    }

    // El marge per trobar un número al fer "click" al mapa (en metres, s'utilitza a la funció processMapClick)
    protected static readonly margin: number = 2.0; 
 
    // Una còpia de les propietats
    protected _props: IPropsMapa;
 
    // La capa de parcel·les (urbana)
    protected parceLayer: OlLayerTile<OlTileSource> | OlLayerImage<OlImageSource>;
 
    // La capa parcel·les (rústica)
    protected rparceLayer: OlLayerTile<OlTileSource> | OlLayerImage<OlImageSource>;
 
    // La capa de selecció (de moment només en MapaState i per parades de mercat)
    protected selLayer: OlLayerTile<OlTileSource> | OlLayerImage<OlImageSource>;
 
    // La nostra projecció
    public olproj: OlProjection;

    // La vista del mapa
    public olview?: OlView; // @observable
 
    // El mapa
    public olmap?: OlMap; // @observable
 
    // La capa de noms
    public nomsLayer?: OlLayerTile<OlTileSource> | OlLayerImage<OlImageSource>;

    // La configuració del mapa
    public mapConfig: IMapConfig = new EmptyMapConfig();
 
    // La llista de capes base
    public baseLayers: Array<OlLayerTile<OlTileSource> | OlLayerImage<OlImageSource>> = []; // @observable
 
    // La llista de capes de referència
    public refLayers: Array<OlLayerTile<OlTileSource> | OlLayerImage<OlImageSource>> = []; // @observable

    // Request
    public req: AjtAjaxAxios = new AjtAjaxAxios();
 
    // L'interface cap a l'API per cercar adreces i referències cadastrals
    public refCadWebApi: RefCadAdrWidgetAPI = null;
 
   // La capa (el source) amb el marcador
    public markerSource: OlSourceVector;

    // llista de fotos d'un parcel·la
    public photoArray: string[] = []; // @observable

    // Usuari per les cridesd al WS
    public usr = 'AJT'; 

    // Sessió per les crides als WS
    public ses = md5(this.usr + Util.getDataYYYYMMDD());

    // Per saber si la configuració s'ha carregat bé
    @observable public configOk = false;

    // L'amplada de la pàgina per calcular l'amplada del mapa
    @observable width = 800;
 
    // L'alçada' de la pàgina per calcular l'alçada' del mapa
    @observable height = 600;
 
    // L'ultim numcod triat
    @observable numCod = 0;
 
    // L'ultim fincod triat
    @observable finCod = 0;

    // Ultima referència cadastral (1) triada (sincronitzat amb el finCod)
    @observable finRefCad1 = '';
 
    // Ultima referència cadastral (2) triada (sincronitzat amb el finCod)
    @observable finRefCad2 = '';
 
    // Ultima parada de mercat (ClasNum de moment només en MapaState)
    @observable pParada = '';

    // Variable per guardar el FID de la capa de parcel·les
    @observable parceFid = '';
 
    // Variable per guardar el FID de la capa de parcel·les de rústica
    @observable rparceFid = '';
 
    // Diu si estem identificant la posició
    @observable identPosition = 0;
 
    // Diu si estem identificant un element
    @observable identElement = false;
 
    // Per controlar l'estat del HorizonCollapse
    @observable hcExpanded =  false;
 
    // La posició d'interès (del WorkFLow)
    @observable position?: Coordinate;
 
    // Per saber quines capes de referència hi ha actives
    @observable actRefLayers: Array<boolean> = [];
 
    // Diu si es veu el checkbox de la capa de noms
    @observable showNoms = false;

    // Diu si la capa de noms estava visible
    @observable nomsVisible = false;

    // Adreça de la Foto que estem ensenyant a la pantalla
    @observable photoAdr = '';

     // Foto que estem ensenyant a la pantalla
    @observable photoIndex = -1

    // Posa el marcador on digui position
    protected setMarker() {
        if (this.markerSource) {
            this.markerSource.clear(true);
            if (this.position) {
                const feature = new OlFeature(new OlGeomPoint([...this.position]));
                this.markerSource.addFeature(feature);
            }
        }
    }
 
     // Creem la capa de marcadors
    public addMarkerLayer() {
        this.markerSource = new OlSourceVector();
        const olLy: OlLayerVector<OlSourceVector> = new OlLayerVector({
            source: this.markerSource,
            style: [
                new OlStyleStyle({
                    image: new OlStyleIcon({
                        src: myShadow,
                        imgSize: [45, 26],
                        anchor: [0, 26],
                        anchorXUnits: IconAnchorUnits.PIXELS,
                        anchorYUnits: IconAnchorUnits.PIXELS
                    })
                }), 
                new OlStyleStyle({
                    image: new OlStyleIcon({
                    src: myIcon,
                    imgSize: [27, 39],
                    anchor: [14, 39],
                    anchorXUnits: IconAnchorUnits.PIXELS,
                    anchorYUnits: IconAnchorUnits.PIXELS
                    })
                })
            ]
        });
        olLy.set('name', 'marker');
        olLy.set('singleTile', true);
        olLy.set('visible', true);
        olLy.setZIndex(999);
        this.olmap.addLayer(olLy);
    }
 
    // Retorna el Index d'un refLayer
    public getRefLayerIndex(id: string): number {
        let i = -1;
        this.refLayers.find((l, index) => {
            const lid = l.get('id');
            if (id === lid) {
                i = index;
                return true;
            } else {
                return false;
            }
        });
        return i;
    }

    // Posa el FID de l'element seleccionat als paràmetres del Layer
    public setSelect(layer: OlLayerTile<OlTileSource> | OlLayerImage<OlImageSource>, fid: string) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const src: any = layer.getSource();
        if (fid) {
            if (src.params_.SELECT) {
                if (src.params_.SELECT === fid) {
                    return; // Ja teníem el mateix element seleccionat
                }
                delete src.params_.SELECT;
            }
            src.params_.SELECT = fid;
            src.refresh();
        } else {
            if (src.params_.SELECT) {
                delete src.params_.SELECT;
                src.refresh();
            }
        }
    }
    
    // Retorna l'objecte 'parms' parametres extres per crear eun WMS layer
    public getLayerExtraParms(ly: ILayer) {
        if (ly && ly.type === 'wms' && ly.qfilter) {
            return {qfilter: ly.qfilter};
        }
        return null;
    }

    // Creem un layer WMS al mapa
    public createWMSLayer(id: string, name: string, url: string, parms: object, layers: string, singleTile: boolean, transparent: boolean, visible: boolean, opacity: number, info: boolean, minZ: number, maxZ: number): OlLayerTile<OlTileSource> | OlLayerImage<OlImageSource> { // @observable
        let olLy: OlLayerTile<OlTileSource> | OlLayerImage<OlImageSource>;
        if (singleTile) {
            olLy = new OlLayerImage({
                source: new OlSourceImageWMS({
                    url: url, 
                    params: {layers: layers, SRS: this.olproj.getCode(), FORMAT: 'image/png', TRANSPARENT: transparent, ...parms}, // Nom de la capa en el servidor WMS, format d'imatge PNG, és una capa base, volem que sigui visible)
                    projection: this.olproj
                }),
                opacity: opacity,
                minZoom: minZ,
                maxZoom: maxZ
            });
        } else {
            olLy = new OlLayerTile({
                source: new OlSourceTileWMS({
                    url: url, 
                    params: {layers: layers, SRS: this.olproj.getCode(), FORMAT: 'image/png', TRANSPARENT: transparent, ...parms}, // Nom de la capa en el servidor WMS, format d'imatge PNG, és una capa base, volem que sigui visible)
                    projection: this.olproj
                }),
                opacity: opacity,
                minZoom: minZ,
                maxZoom: maxZ
            });
        }
        // EL nom s'ha de posar despres ! No està al options
        olLy.set('name', name);
        olLy.set('singleTile', singleTile); // Ens guardem si és singleTile
        olLy.set('visible', visible); // Si d'entrada és visible o no
        olLy.set('id', id); // Id de la capa
        olLy.set('info', info); // Ens guardem si es pot fer un GetFeatureInfo
        return olLy;
    }

    // Creem una capa WMTS
    public createWMTSLayer(id: string, name: string, cap: Options, visible: boolean, opacity: number, minZ: number, maxZ: number): OlLayerTile<OlTileSource> {
        const olLy: OlLayerTile<OlTileSource> = new OlLayerTile({
            source: new OlSourceWMTS(cap),
            opacity: opacity,
            minZoom: minZ,
            maxZoom: maxZ
        });
        olLy.set('name', name);
        olLy.set('visible', visible);
        olLy.set('cap', cap);
        olLy.set('id', id);
        return olLy;
    }

    // Mètodes abstractes que han d'implementar les classes derivades
    abstract createMap();

    abstract derivedSetFIDFromNumCod(numCod: number);

    abstract derivedSetFIDFromFinCod(finCod: number);

    abstract derivedSetFIDFromRefCad(finRefCad1: string, finRefCad2: string);

    abstract derivedSetFIDFromPos(c: Coordinate);

    abstract derivedGetElementFromPos(c: Coordinate, ilY: number);

    // Posa o treu el checkbox del refLayer
    // Si hi ha alguna capa de referència activada aclareix el mapa base.
    // També posa el identElement a true o false de penenent de si hi ha una activada en al que es puguibn consultar els elements
    @action setRefLayerVisible(index: number, show: boolean, dim: boolean) {
        this.actRefLayers[index] = show;
        if (show) {
            if (dim) {
                this.olmap.getLayers().item(0).setOpacity(0.75);
            }
            if (!this.identElement) { 
                const ly: OlLayerTile<OlTileSource> | OlLayerImage<OlImageSource> =  this.refLayers[index];
                if (ly.get('info') && (ly.get('singleTile') || !ly.get('cap'))) {
                    this.setIdentElement(true);
                    return; // Teniem desactivat el identElement, la capa que hem activat sí permet fer-ho => ho activem
                } else {
                    return; // Ja teniem desactivat el identElement, la capa que hem activat no permet fer-ho => ho deixem igual (desactivat)
                }
            } else {
                return; // Ja teniem activat el identElement => ho deixem igual (activat)
            }
        } else {
            const act: number = this.actRefLayers.indexOf(true);
            if (act < 0) {
                if (dim) {
                    this.olmap.getLayers().item(0).setOpacity(1.0);
                }
                if (this.identElement) {
                    this.setIdentElement(false);
                    return; // Teniem activat el identElement i ja no queda cap capa activada => ho desactivem
                }
            }
        }
        // Anem a mirar si queda alguna capa que permeti consultar elements
        for (let i = 0; i < this.actRefLayers.length; i++) {
            if (this.actRefLayers[i]) {
                const ly: OlLayerTile<OlTileSource> | OlLayerImage<OlImageSource> =  this.refLayers[i];
                if (ly.get('info') && (ly.get('singleTile') || !ly.get('cap'))) {
                    if (!this.identElement) {
                        this.setIdentElement(true);
                    }
                    return;
                }
            }
        }
        // No queda cap capa
        if (this.identElement) {
            this.setIdentElement(false);
        }
    }

    // Posa o treu el checkbox dels noms
    @action setShowNoms(show: boolean) {
        this.showNoms = show;
    }
 
    // Posa o treu el checkbox dels noms
    @action setNomsVisible(show: boolean) {
        this.nomsVisible = show;
    }
 
    // Per posar l'estat de l'HorizontalCollapse
    @action setHCExpanded(expanded: boolean) {
        this.hcExpanded = expanded;
    }
 
    // Por posar o treure el mode d'identificar posició
    // 0 -> No identificar posició, 1 -> Identificar posició, 2 - > Mosrar fotografia de la façana
    @action setIdentPosition(identPosition: number) {
        this.identPosition = identPosition;
        if (!identPosition)
        {
            this.position = undefined;
            this.markerSource.clear(true);
            this.numCod = 0;
            this.finCod = 0;
            this.finRefCad1 = '';
            this.finRefCad2 = '';
            this.parceFid = '';
            this.rparceFid = '';
            this.setSelect(this.parceLayer, '');
            this.setSelect(this.rparceLayer, '');
            if (this.selLayer) {
                this.setSelect(this.selLayer, '');
            }
        }
    }
 
    // Por posar o treure el mode d'identificar element
    @action setIdentElement(identElement: boolean) {
        this.identElement = identElement;
    }
 
    @action zoomToPosition() {
        if (this.position) {
            const ext: Extent = [this.position[0] - 1, this.position[1] - 1, this.position[0] + 1, this.position[1] + 1];
            this.olview.fit(ext);
        }
    }
 
    // Actualitza les mides de la pàgina
    @action updateWindowSize() {
        this.width = window.innerWidth;
        this.height = window.innerHeight;
    }
 
    // Posa el zoom i la posició a partir de la finca o adreça seleccionada
    @action setZoomPos(response: string) {
       let txt = Util.getBetween('<gml:boundedBy>', '</gml:boundedBy>', response, {pos: 0});
       if (txt != null) {
           txt = Util.getBetween('<gml:coordinates>', '</gml:coordinates>', response, {pos: 0});
           if (txt != null) {
               txt = txt.replace(/ /g, ',');
               const crds = txt.split(',');
               if (crds.length === 4) {
                   const ext: Extent = [parseFloat(crds[0]), parseFloat(crds[1]), parseFloat(crds[2]), parseFloat(crds[3])];
                   this.olview.fit(ext);
                   this.position = getCenter(ext);
                   this.setMarker();
               }
           }
       }
    }

    // Posa el zoom de la posició seleccionada
    @action setZoomPosCoord(c: Coordinate) {
        const ext: Extent = [c[0] - 1, c[1] - 1, c[0] + 1, c[1] + 1];
        this.olview.fit(ext);
        this.position = [...c];
        this.setMarker();
    }

    // Busquem el FID d'una finca (a partir de la referència cadastral)
    @action setFIDFromRefCad(finRefCad1: string, finRefCad2: string) {
        this.derivedSetFIDFromRefCad(finRefCad1, finRefCad2);
    }

    // Busquem el FID d'una finca (a partir del FinCod)
    @action setFIDFromFinCod(finCod: number) {
        this.derivedSetFIDFromFinCod(finCod);
    }
 
    // Busquem el FID d'un Numero (a partir del NumCod)
    @action setFIDFromNumCod(numCod: number) {
        this.derivedSetFIDFromNumCod(numCod);
    }
 
    // Busquem el FID d'una adreça o d'una finca o d'un element (a partir d'una coordenada que ve d'un event) o posem el marcador
    @action processMapClick = (evt: OlEvent) => {
        if (this.identPosition === 1) {
            const mevt: MapBrowserEvent<UIEvent> = (evt as MapBrowserEvent<UIEvent>);
            this.setFIDFromPos(mevt.coordinate);
        } else if (this.identPosition === 2) {
            const mevt: MapBrowserEvent<UIEvent> = (evt as MapBrowserEvent<UIEvent>);
            this.setFIDFromPosPhoto(mevt.coordinate);
        } else if (this.identElement) {
            const mevt: MapBrowserEvent<UIEvent> = (evt as MapBrowserEvent<UIEvent>);
            this.getElementFromPos(mevt.coordinate);
        }
    }
     
    // Busquem el FID d'una adreça i/o d'una finca (a partir d'una coordenada)
    // Aquesta funció és la que es crida quan s'ha fet un "click" al mapa amb identPosition = 1
    @action setFIDFromPos = (c: Coordinate) => {
        if (c[0] > 0 && c[0] < 2 && c[1] > 40 && c[1] < 42) { // Era una coordenada LonLat
            c = proj4('EPSG:4326', this.olproj.getCode()).forward(c);
        }
        this.derivedSetFIDFromPos(c);
    }
     
    // Busquem el FID d'una finca d'urban (a partir d'una coordenada) i si la trobem mostrem la foto de la finca i si pot ser l'adreça
    @action setFIDFromPosPhoto = (c: Coordinate) => {
        const xml = '<?xml version="1.0" ?><GetFeature version="1.1.0" service="WFS" outputFormat="GML2" xmlns:ogc="http://www.opengis.net/ogc" xmlns:gml="http://www.opengis.net/gml"><Query typeName="' + 
                    this.mapConfig.parceLayer.layers + '" srsName="EPSG:25831"><Filter><Intersects><PropertyName>GEOM</PropertyName><gml:Point gid="1" srsName="EPSG:25831"><gml:coordinates>' + 
                    c[0].toString() + ',' + c[1].toString() + '</gml:coordinates></gml:Point></Intersects></Filter></Query></GetFeature>';
        this.req.postRequestRaw(this.mapConfig.wfsUrl, xml, { headers: {'Content-Type': 'text/html;'} }).then((result) => {
            const finRefCad1 = Util.getBetweenI('FINREFCAD1>', '<', result as string, {pos: 0});
            const finRefCad2 = Util.getBetweenI('FINREFCAD2>', '<', result as string, {pos: 0});
            if (finRefCad1 && finRefCad2) {
                const xml2 = '<?xml version="1.0" encoding="utf-8"?><soap:Envelope xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:xsd="http://www.w3.org/2001/XMLSchema" xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/"><soap:Body><GetPhotoList xmlns="http://www.ajmanresa.cat/GuiaWS"><pXML>' + Util.encodeHTML('<WSParam><Session>' + this.ses + '</Session><Usuari>' + this.usr + '</Usuari><RefPar>' + finRefCad1 + finRefCad2 + '</RefPar></WSParam>') + '</pXML></GetPhotoList></soap:Body></soap:Envelope>';
                this.req.postRequestRaw(this.mapConfig.wsGuia, xml2, { headers: {'Content-Type': 'text/xml; charset=utf-8'} }).then((result2) => {
                    const photoList: string = Util.getBetween("<GetPhotoListResult>", "</", result2 as string, {pos: 0});
                    if (photoList) {
                        this.photoArray = photoList.split(";");
                        if (this.photoArray.length > 0) {
                            this.photoIndex = 0;
                            // Mirem si trobem l'adreça de la foto
                            const xml3 = '<?xml version="1.0" encoding="utf-8"?><soap:Envelope xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:xsd="http://www.w3.org/2001/XMLSchema" xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/"><soap:Body><GetAdrecaByRefPar xmlns="http://www.ajmanresa.cat/GuiaWS"><pXML>' + Util.encodeHTML('<WSParam><Session>' + this.ses + '</Session><Usuari>' + this.usr + '</Usuari><RefPar>' + finRefCad1 + finRefCad2 + '</RefPar></WSParam>') + '</pXML></GetAdrecaByRefPar></soap:Body></soap:Envelope>';
                            this.req.postRequestRaw(this.mapConfig.wsGuia, xml3, { headers: {'Content-Type': 'text/xml; charset=utf-8'} }).then((result3) => {
                                this.photoAdr = Util.getBetween("<GetAdrecaByRefParResult>", "</", result3 as string, {pos: 0});
                            }).catch(() => { /* No fa res*/ });
                        }
                    }
                }).catch(() => { /* No fa res*/ });
            }
        }).catch(() => { /* No fa res*/ }); 
        this.identPosition = 0;
    }
     
    // Llegim un element a partir d'una coordenada
    @action getElementFromPos = (c: Coordinate) => {
        this.derivedGetElementFromPos(c, 0);
    }
    
}
